import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import moment from "moment";
import PublishIcon from "@material-ui/icons/Publish";
import { presentDate } from "../functionalComponents/dataFormatFunctions";
import jwtDecode from "jwt-decode";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "16px",
    border: "1px solid #ddd",
  },
  fileLink: {
    display: "flex",
    alignItems: "center",
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  remarks: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  divider: {
    borderTop: "1px solid #ddd",
    margin: theme.spacing(1, 0),
  },
  button: {
    backgroundColor: "#FF9677",
    color: "#fff",
    marginTop: theme.spacing(1),
    "&:hover": {
      backgroundColor: "#FF9677",
    },
  },
}));

const VersionHistory = (props) => {
  const {
    viewVersionHistoryScreen,
    handleViewVersionHistory,
    cdmoFileDetails,
    deliverablesData,
    handleFileClick,
  } = props;

  const tokenData = jwtDecode(localStorage.getItem("authToken"));

  const classes = useStyles();

  const versionNumbers = cdmoFileDetails.map((file) => file.versionNumber);
  const lowestVersion = Math.min(...versionNumbers);
  const highestVersion = Math.max(...versionNumbers);

  // Set the initial selectedVersion to the lowest version
  const [selectedVersion, setSelectedVersion] = useState(lowestVersion); // Convert to zero-based index

  const [selectedFile, setSelectedFile] = useState(
    cdmoFileDetails.find(
      (item) => item.versionNumber === 1 && item.fileType === "scientific_file"
    )
  );

  const [supportingFiles, setSupportingFiles] = useState(
    cdmoFileDetails.filter((file) => file.fileType === "supporting_file")
  );

  const handleTabChange = (newValue) => {
    const newVersion = newValue + 1; // Convert zero-based index to version number
    setSelectedVersion(newVersion);
    const file = cdmoFileDetails.find(
      (item) =>
        item.versionNumber === newVersion && item.fileType === "scientific_file"
    );
    setSelectedFile(file || null);
  };

  return (
    <Dialog open={viewVersionHistoryScreen} fullWidth maxWidth="lg">
      <DialogTitle
        disableTypography
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography style={{ fontSize: "36px", fontWeight: 500 }}>
          View versions history
        </Typography>

        <Box
          onClick={handleViewVersionHistory}
          display="flex"
          alignItems="center"
          style={{ cursor: "pointer" }}
        >
          <Typography
            variant="body2"
            style={{
              color: "black",
              marginRight: 4,
              textDecoration: "none",
            }}
            onMouseOver={(e) => {
              e.target.style.color = "red";
              e.target.style.textDecoration = "underline";
            }}
            onMouseOut={(e) => {
              e.target.style.color = "black";
              e.target.style.textDecoration = "none";
            }}
          >
            Cancel
          </Typography>
          <CloseIcon style={{ color: "red" }} />
        </Box>
      </DialogTitle>
      <DialogTitle>
        <Tabs
          value={selectedVersion - 1} // Zero-based index for initial tab
          onChange={(event, newValue) => handleTabChange(newValue)}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
          //   scrollButtons="on"
        >
          {[...Array(highestVersion)].map((_, index) => (
            <Tab
              key={index}
              label={`Version ${index + 1}`}
              style={{
                textAlign: "left",
                minWidth: "120px",
                textTransform: "none",
              }}
            />
          ))}
        </Tabs>
      </DialogTitle>

      <DialogContent>
        <Box margin={1}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography variant="h6">
                {deliverablesData.cdmoOrgName}
              </Typography>
              <Paper elevation={2} className={classes.paper}>
                {selectedVersion && (
                  <React.Fragment>
                    <Grid container spacing={3}>
                      {/* Version and Date (Left-aligned) */}
                      <Grid item xs={4}>
                        <Typography variant="subtitle1">
                          Version {selectedVersion}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {moment(selectedFile.lastUpdatedDate).format(
                            "(YYYY-MM-DD HH:mm:ss)"
                          )}
                        </Typography>
                      </Grid>

                      {/* File Name (Right-aligned) */}
                      <Grid item xs={8} className={classes.fileLink}>
                        <PublishIcon className={classes.icon} />
                        <Link
                          href={selectedFile.fileLink}
                          target="_blank"
                          rel="noopener"
                          color="secondary"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent default link action
                            handleFileClick(selectedFile); // Trigger the state change to open the component
                          }}
                        >
                          {selectedFile.fileName}
                        </Link>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid item xs={4}>
                        <Typography variant="body2" className={classes.remarks}>
                          Upload Remarks
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" className={classes.remarks}>
                          {selectedFile.uploadRemarks
                            ? selectedFile.uploadRemarks
                            : "-NA-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                )}

                <Grid container spacing={3}>
                  <Grid xs={12}>
                    <div className={classes.divider}></div>
                  </Grid>
                </Grid>

                {/* Supporting Files */}
                <Grid container spacing={3}>
                  {supportingFiles.map((file, index) => (
                    <>
                      <Grid item xs={4}>
                        <Typography
                          variant="subtitle1"
                          style={{ marginTop: "20px" }} // Added margin for better spacing
                        >
                          {index === 0 ? "Supporting Files" : ""}
                        </Typography>
                      </Grid>

                      <Grid
                        container
                        item
                        xs={8}
                        key={index}
                        alignItems="flex-start"
                        className={classes.fileLink}
                        style={{ marginTop: "20px" }} // Added margin for better spacing
                      >
                        {/* File Icon and Name */}
                        <Grid container alignItems="center">
                          <PublishIcon className={classes.icon} />
                          <Link
                            href={file.fileLink}
                            target="_blank"
                            rel="noopener"
                            style={{
                              marginRight: "8px",
                              cursor: "pointer",
                            }}
                            color="secondary"
                            onClick={(e) => {
                              e.preventDefault(); // Prevent default link action
                              handleFileClick(file); // Trigger the state change to open the component
                            }}
                          >
                            {file.fileName}
                          </Link>
                        </Grid>

                        {/* Date below the file name */}
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            {moment(file.lastUpdatedDate).format(
                              "(YYYY-MM-DD HH:mm:ss)"
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  ))}
                </Grid>
              </Paper>
            </Grid>
            {selectedFile && (
              <Grid item xs={6}>
                <Typography variant="h6">
                  {deliverablesData.cdmoOrgName}
                </Typography>
                <Paper elevation={2} className={classes.paper}>
                  {selectedFile &&
                    // selectedFile.qaDecision !== "" &&
                    !selectedFile?.approvalDataQAandSME.filter(
                      (val) =>
                        val.stakeHolderType === "Internal" &&
                        val.enableApprovalFlag === true &&
                        val.decisionBy === tokenData.email_address
                    ).length > 0 &&
                    selectedFile?.approvalDataQAandSME.filter(
                      (val) =>
                        val.stakeHolderType === "Internal" &&
                        val.requirementType === "Approve" &&
                        (val.decision === "Approved" ||
                          val.decision === "Rejected")
                    ).length > 0 &&
                    // localStorage.collabType !== "Pharma Co" &&
                    selectedFile?.approvalDataQAandSME
                      .filter(
                        (val) =>
                          val.stakeHolderType === "Internal" &&
                          val.requirementType === "Approve" &&
                          (val.decision === "Approved" ||
                            val.decision === "Rejected")
                      )
                      .map((val) => (
                        <>
                          <Grid container spacing={3}>
                            <Grid item xs={4}>
                              <Typography
                                variant="body2"
                                className={classes.remarks}
                              >
                                Supporting files
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography
                                variant="body2"
                                className={classes.remarks}
                              >
                                NA
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid container spacing={3}>
                            <Grid item xs={4}>
                              <Typography
                                variant="body2"
                                className={classes.remarks}
                              >
                                Approve/Reject Remarks
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography
                                variant="body2"
                                className={classes.remarks}
                              >
                                {val?.docusignRemarks && val?.docusignRemarks !=='' ? val?.docusignRemarks : val?.remarks && val?.remarks !== ""
                                  ? val?.remarks
                                  : "NA"}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Grid container spacing={3}>
                            <Grid item xs={4}>
                              <Typography
                                variant="body2"
                                className={classes.remarks}
                              >
                                {val?.decision === "Approved"
                                  ? "Approved by"
                                  : "Rejected by"}
                              </Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography
                                variant="body2"
                                className={classes.remarks}
                              >
                                {val?.docusignRemarks && val?.docusignRemarks !== '' ?
                                                      `${val?.userName} on ${val?.decisionDate ? moment(val?.decisionDate).format("DD-MMM-YYYY").toUpperCase():' - '} in Docusign (though initially approved in Mosymphony)`
                                                      :`${val?.userName} on ${val?.decisionDate ? moment(val?.decisionDate).format("DD-MMM-YYYY").toUpperCase():' - '}`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </>
                      ))}

                  {selectedFile &&
                    // selectedFile.qaDecision === "" &&
                    selectedFile?.approvalDataQAandSME.filter(
                      (val) =>
                        val.stakeHolderType === "Internal" &&
                        val.requirementType === "Approve" &&
                        (val.decision === "" || val.decision === null)
                    ).length > 0 &&
                    // localStorage.collabType !== "Pharma Co" &&
                    selectedFile?.approvalDataQAandSME
                      .filter(
                        (val) =>
                          val.enableApprovalFlag === true &&
                          val.decisionBy === tokenData.email_address &&
                          val.stakeHolderType === "Internal" &&
                          val.requirementType === "Approve" &&
                          (val.decision === "" || val.decision === null)
                      )
                      .map((val) => (
                        <React.Fragment>
                          <Grid
                            container
                            spacing={3}
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Grid item xs={4}>
                              <Typography
                                variant="body2"
                                className={classes.remarks}
                              >
                                Supporting files
                              </Typography>
                            </Grid>
                          </Grid>
                        </React.Fragment>
                      ))}

                  {selectedFile &&
                    !selectedFile?.approvalDataQAandSME.filter(
                      (val) =>
                        val.stakeHolderType === "Internal" &&
                        val.enableApprovalFlag === true &&
                        val.decisionBy === tokenData.email_address
                    ).length > 0 &&
                    selectedFile?.approvalDataQAandSME.filter(
                      (val) =>
                        val.stakeHolderType === "Internal" &&
                        val.requirementType === "Approve" &&
                        (val.decision === "" || val.decision === null)
                    ).length > 0 &&
                    // localStorage.collabType !== "Pharma Co" &&
                    (
                      <>
                        {selectedFile?.approvalDataQAandSME
                          .filter(
                            (val) =>
                              val.stakeHolderType === "Internal" &&
                              val.requirementType === "Approve" &&
                              (val.decision === "" || val.decision === null)
                          )
                          .map((val) =>
                            val.decisionBy === tokenData.email_address &&
                            val.enableApprovalFlag === true ? (
                              <></>
                            ) : (
                              <Grid container spacing={3}>
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    className={classes.remarks}
                                  >
                                    {val.requirementType === "Approve"
                                      ? "Approval"
                                      : "Review"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                  <Typography
                                    variant="body2"
                                    className={classes.remarks}
                                  >
                                    {selectedFile?.approvalDataQAandSME
                                              .filter(
                                                (val) =>
                                                  val.requirementType === "Approve" && val.decision === "Rejected"
                                                            ).length > 0 ?
                                                              `Not to be assigned to ${val.userName} because ${selectedFile?.approvalDataQAandSME.filter(
                                                  (val) =>
                                                    val.requirementType === "Approve" && val.decision === "Rejected"
                                                )[0].userName} rejected the document at ${presentDate(selectedFile?.approvalDataQAandSME.filter(
                                                  (val) =>
                                                    val.requirementType === "Approve" && val.decision === "Rejected"
                                                )[0].decisionDate)}` : val.enableApprovalFlag
                                      ? `Assigned to ${val.userName} ( ${val.userRole} )`
                                      : `To be assigned to ${val.userName} ( ${val.userRole} )`}
                                  </Typography>
                                </Grid>
                              </Grid>
                            )
                          )}
                      </>
                    )}
                </Paper>

                {!selectedFile?.approvalDataQAandSME.filter(
                  (val) =>
                    val.stakeHolderType === "Internal" &&
                    val.enableApprovalFlag === true &&
                    val.decisionBy === tokenData.email_address
                ).length > 0 && (
                  <React.Fragment>
                    <Typography variant="h6" style={{ marginTop: "2rem" }}>
                      {deliverablesData.sponsorOrgName}
                    </Typography>
                    <Paper elevation={2} className={classes.paper}>
                      {selectedFile &&
                        // selectedFile.qaDecision !== "" &&
                        !selectedFile?.approvalDataQAandSME.filter(
                          (val) =>
                            val.stakeHolderType === "External" &&
                            val.enableApprovalFlag === true &&
                            val.decisionBy === tokenData.email_address
                        ).length > 0 &&
                        selectedFile?.approvalDataQAandSME.filter(
                          (val) =>
                            val.stakeHolderType === "External" &&
                            val.requirementType === "Approve" &&
                            (val.decision === "Approved" ||
                              val.decision === "Rejected")
                        ).length > 0 &&
                        localStorage.collabType !== "Pharma Co" &&
                        selectedFile?.approvalDataQAandSME
                          .filter(
                            (val) =>
                              val.stakeHolderType === "External" &&
                              val.requirementType === "Approve" &&
                              (val.decision === "Approved" ||
                                val.decision === "Rejected")
                          )
                          .map((val) => (
                            <>
                              <Grid container spacing={3}>
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    className={classes.remarks}
                                  >
                                    Approve/Reject Remarks
                                  </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                  <Typography
                                    variant="body2"
                                    className={classes.remarks}
                                  >
                                    {val?.docusignRemarks && val?.docusignRemarks !=='' ? val?.docusignRemarks : val?.remarks && val?.remarks !== ""
                                      ? val?.remarks
                                      : "NA"}
                                  </Typography>
                                </Grid>
                              </Grid>

                              <Grid container spacing={3}>
                                <Grid item xs={4}>
                                  <Typography
                                    variant="body2"
                                    className={classes.remarks}
                                  >
                                    {val?.decision === "Approved"
                                      ? "Approved by"
                                      : "Rejected by"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                  <Typography
                                    variant="body2"
                                    className={classes.remarks}
                                  >
                                    {val?.docusignRemarks && val?.docusignRemarks !== '' ?
                                                      `${val?.userName} on ${val?.decisionDate ? moment(val?.decisionDate).format("DD-MMM-YYYY").toUpperCase():' - '} in Docusign (though initially approved in Mosymphony)`
                                                      :`${val?.userName} on ${val?.decisionDate ? moment(val?.decisionDate).format("DD-MMM-YYYY").toUpperCase():' - '}`}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </>
                          ))}

                      {selectedFile &&
                        selectedFile?.approvalDataQAandSME.filter(
                          (val) =>
                            val.stakeHolderType === "External" &&
                            (val.decision === "" || val.decision === null)
                        ).length > 0 &&
                        // localStorage.collabType !== "Pharma Co" &&
                        (
                          <>
                            {selectedFile?.approvalDataQAandSME
                              .filter(
                                (val) =>
                                  val.stakeHolderType === "External" &&
                                  (val.decision === "" || val.decision === null)
                              )
                              .map((val) => (
                                <Grid container spacing={3}>
                                  <Grid item xs={4}>
                                    <Typography
                                      variant="body2"
                                      className={classes.remarks}
                                    >
                                      {val.requirementType === "Approve"
                                        ? "Approval"
                                        : "Approval"}{" "}
                                      {val.userId ===
                                      deliverablesData.sponsorSMESignatoryID
                                        ? " - SME "
                                        : " - QA"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={8}>
                                    <Typography
                                      variant="body2"
                                      className={classes.remarks}
                                    >
                                      {selectedFile?.approvalDataQAandSME
                                              .filter(
                                                (val) =>
                                                  val.requirementType === "Approve" && val.decision === "Rejected"
                                                            ).length > 0 ?
                                                              `Not to be assigned to ${val.userName} because ${selectedFile?.approvalDataQAandSME.filter(
                                                  (val) =>
                                                    val.requirementType === "Approve" && val.decision === "Rejected"
                                                )[0].userName} rejected the document at ${presentDate(selectedFile?.approvalDataQAandSME.filter(
                                                  (val) =>
                                                    val.requirementType === "Approve" && val.decision === "Rejected"
                                                )[0].decisionDate)}` : val.enableApprovalFlag
                                        ? `Assigned to ${val.userName} ( ${val.userRole} )`
                                        : `To be assigned to ${val.userName} ( ${val.userRole} )`}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              ))}
                          </>
                        )}
                    </Paper>
                  </React.Fragment>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistory;
